import { Component } from '@theme/component';
import { debounce, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} ActiveState
 * @property {HTMLElement} owner - The top-level list item that owns the state.
 * @property {HTMLElement} trigger - The top-level menu trigger.
 * @property {HTMLElement | null} submenu - The submenu surface controlled by the trigger.
 * @property {boolean} isOverflow - Whether the owner is rendered inside the overflow menu.
 * @property {boolean} hasOwnSubmenu - Whether the owner has a dedicated submenu.
 * @property {number} generation - The state generation used to reject stale async work.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /** @type {ActiveState | null} */
  #state = null;

  #generation = 0;
  #initialRecoveryFrame;
  #geometryFrame;
  #closeTimer;
  #suppressFocusOpen = false;
  #touchPointerOwner = null;
  #touchOwnerWasActive = false;

  /** @type {ResizeObserver | null} */
  #submenuResizeObserver = null;

  /** @type {HTMLElement | null} */
  #overflowSurface = null;

  connectedCallback() {
    super.connectedCallback();

    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointerover', this.#onPointerOver);
    this.addEventListener('pointerout', this.#onPointerOut);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    window.dispatchEvent(new Event('insmia:navigation:initialize'));
    this.#connectOverflowSurface();
    this.#initialRecoveryFrame = requestAnimationFrame(this.#recoverInitialItem);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('pointerover', this.#onPointerOver);
    this.removeEventListener('pointerout', this.#onPointerOut);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
    if (this.#initialRecoveryFrame) {
      cancelAnimationFrame(this.#initialRecoveryFrame);
      this.#initialRecoveryFrame = undefined;
    }
    if (this.#state) {
      this.#closeActive(this.#state.generation);
    } else {
      this.#cancelClose();
      this.#cancelGeometryFrame();
      this.#cleanupResizeObserver();
    }
    this.#disconnectOverflowSurface();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    if (this.#state) this.#queueGeometrySync(this.#state);
  }, 100);

  #recoverInitialItem = () => {
    this.#initialRecoveryFrame = undefined;
    this.#connectOverflowSurface();
    const hoveredOrFocusedItem = Array.from(this.querySelectorAll('.menu-list__list-item')).find(
      (item) => item.matches(':hover') || item.matches(':focus-within')
    );
    if (hoveredOrFocusedItem instanceof HTMLElement) {
      this.#openOwner(hoveredOrFocusedItem);
    }
  };

  /**
   * Open the top-level item under the pointer. Pointerover is delegated so moving
   * through descendants does not install per-item listeners.
   * @param {PointerEvent} event
   */
  #onPointerOver = (event) => {
    if (event.pointerType === 'touch' || !(event.target instanceof Element)) return;

    const owner = this.#resolveOwner(event.target);
    if (!owner) return;

    this.#cancelClose();
    this.#openOwner(owner);
  };

  /**
   * Record whether a coarse-pointer tap started on an already open item. Focus
   * may move before click, so click cannot infer first-tap state on its own.
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse' || !(event.target instanceof Element)) return;

    const owner = this.#resolveOwner(event.target);
    this.#touchPointerOwner = owner;
    this.#touchOwnerWasActive = Boolean(owner && this.#state?.owner === owner);
  };

  /**
   * On touch-first desktop layouts, the first tap opens the submenu and the
   * second tap follows the link. Mouse clicks continue to navigate normally.
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const owner = this.#resolveOwner(event.target);
    if (!owner) return;

    const trigger = findMenuTrigger(owner);
    if (!trigger || !findSubmenu(trigger)) return;

    const isRecordedTouch = owner === this.#touchPointerOwner;
    const isCoarsePointer = window.matchMedia('(hover: none), (pointer: coarse)').matches;
    if (!isRecordedTouch && !isCoarsePointer) return;

    const wasActiveBeforeTap = isRecordedTouch ? this.#touchOwnerWasActive : this.#state?.owner === owner;
    this.#touchPointerOwner = null;
    this.#touchOwnerWasActive = false;
    if (wasActiveBeforeTap) return;

    event.preventDefault();
    this.#cancelClose();
    this.#openOwner(owner);
  };

  /**
   * Schedule a close only when the pointer leaves the complete active owner.
   * A late pointerout from a previously active owner is ignored.
   * @param {PointerEvent} event
   */
  #onPointerOut = (event) => {
    if (event.pointerType === 'touch' || !(event.target instanceof Element)) return;

    const owner = this.#resolveOwner(event.target);
    const state = this.#state;
    if (!owner || !state || owner !== state.owner) return;
    if (event.relatedTarget instanceof Node && this.#isNodeInsideState(event.relatedTarget, state)) return;

    this.#scheduleClose(state);
  };

  /**
   * Open the top-level item receiving keyboard focus.
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    if (this.#suppressFocusOpen || !(event.target instanceof Element)) return;

    const owner = this.#resolveOwner(event.target);
    if (!owner) return;

    this.#cancelClose();
    this.#openOwner(owner);
  };

  /**
   * Schedule a close only when focus leaves the complete active owner.
   * @param {FocusEvent} event
   */
  #onFocusOut = (event) => {
    if (!(event.target instanceof Element)) return;

    const owner = this.#resolveOwner(event.target);
    const state = this.#state;
    if (!owner || !state || owner !== state.owner) return;
    if (event.relatedTarget instanceof Node && this.#isNodeInsideState(event.relatedTarget, state)) return;

    this.#scheduleClose(state);
  };

  #onKeyDown = (event) => {
    const state = this.#state;
    if (event.key !== 'Escape' || !state) return;

    event.preventDefault();
    const focusTarget = state.isOverflow ? this.#moreButton : state.trigger;
    this.#suppressFocusOpen = true;
    this.#closeActive(state.generation);
    focusTarget?.focus({ preventScroll: true });
    queueMicrotask(() => {
      this.#suppressFocusOpen = false;
    });
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  get #moreButton() {
    const button = this.querySelector('[slot="more"] > button');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /**
   * Resolve an event target to its top-level owner. The More trigger represents
   * the first item currently assigned to the overflow slot.
   * @param {Element} element
   * @returns {HTMLElement | null}
   */
  #resolveOwner(element) {
    const listItem = element.closest('.menu-list__list-item');
    if (!(listItem instanceof HTMLElement) || !this.contains(listItem)) return null;

    if (listItem.slot === 'more') {
      const firstOverflowItem = this.querySelector('.menu-list__list-item[slot="overflow"]');
      return firstOverflowItem instanceof HTMLElement ? firstOverflowItem : null;
    }

    return findMenuTrigger(listItem) ? listItem : null;
  }

  /**
   * Build and open the single authoritative top-level state.
   * @param {HTMLElement} owner
   */
  #openOwner(owner) {
    if (!this.headerComponent) return;

    const resolvedOwner = owner.slot === 'more' ? this.#resolveOwner(owner) : owner;
    if (!resolvedOwner) return;

    const trigger = findMenuTrigger(resolvedOwner);
    if (!trigger) return;

    this.#cancelClose();

    if (this.#state?.owner === resolvedOwner) {
      this.#queueGeometrySync(this.#state);
      return;
    }

    const ownSubmenu = findSubmenu(trigger);
    const isOverflow = resolvedOwner.slot === 'overflow';
    const submenu = ownSubmenu ?? (isOverflow ? this.overflowMenu : null);
    const state = {
      owner: resolvedOwner,
      trigger,
      submenu,
      isOverflow,
      hasOwnSubmenu: Boolean(ownSubmenu),
      generation: ++this.#generation,
    };

    this.dispatchEvent(new MegaMenuHoverEvent());
    this.#cancelGeometryFrame();
    this.#cleanupResizeObserver();
    this.#clearActiveDom(this.#state);

    this.#state = state;
    this.ariaExpanded = 'true';
    state.trigger.ariaExpanded = 'true';
    this.#setOverflowExpanded(state.isOverflow);

    if (state.submenu) {
      state.submenu.querySelectorAll('img[loading="lazy"]').forEach((image) => image.removeAttribute('loading'));
      state.submenu.dataset.active = '';
    }

    this.style.setProperty('--submenu-opacity', '1');
    this.#syncGeometry(state);
    this.#observeSubmenuSize(state);
    this.#queueGeometrySync(state);
  }

  /**
   * Close the active state after a cancelable 120 ms bridge. The owner and
   * generation captured here prevent an old leave event from closing a new item.
   * @param {ActiveState} state
   */
  #scheduleClose(state) {
    if (!this.#isCurrentState(state)) return;

    this.#cancelClose();
    const { owner, generation } = state;
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = undefined;
      const current = this.#state;
      if (!current || current.owner !== owner || current.generation !== generation) return;
      if (this.#isInteractionInsideState(current)) return;
      this.#closeActive(generation);
    }, 120);
  }

  #cancelClose() {
    if (this.#closeTimer === undefined) return;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
  }

  /**
   * Close only the generation requested by the caller.
   * @param {number} expectedGeneration
   */
  #closeActive(expectedGeneration) {
    const state = this.#state;
    if (!state || state.generation !== expectedGeneration) return;

    this.#cancelClose();
    this.#cancelGeometryFrame();
    this.#cleanupResizeObserver();
    this.#clearActiveDom(state);
    this.#state = null;
    this.#generation += 1;

    this.ariaExpanded = 'false';
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.#setOverflowExpanded(false);
  }

  /**
   * Remove the derived DOM state owned by one generation.
   * @param {ActiveState | null} state
   */
  #clearActiveDom(state) {
    if (!state) return;
    state.trigger.ariaExpanded = 'false';
    if (state.submenu) delete state.submenu.dataset.active;
  }

  /**
   * @param {boolean} expanded
   */
  #setOverflowExpanded(expanded) {
    this.dataset.overflowExpanded = expanded.toString();
    const moreButton = this.#moreButton;
    if (moreButton) moreButton.ariaExpanded = expanded.toString();
  }

  /**
   * @param {ActiveState} state
   */
  #isCurrentState(state) {
    return this.#state?.owner === state.owner && this.#state.generation === state.generation;
  }

  /**
   * @param {Node} node
   * @param {ActiveState} state
   */
  #isNodeInsideState(node, state) {
    if (state.owner.contains(node)) return true;
    if (!state.isOverflow) return false;

    return Boolean(
      this.#moreButton?.contains(node) ||
        this.#overflowSurface?.contains(node) ||
        this.refs.overflowMenu?.contains(node)
    );
  }

  /**
   * Check pointer and focus at timer execution so crossing the small visual gap
   * between a trigger and its submenu remains cancelable without hit-test overlays.
   * @param {ActiveState} state
   */
  #isInteractionInsideState(state) {
    const activeElement = document.activeElement;
    const focusInside =
      activeElement instanceof Node && this.#isNodeInsideState(activeElement, state);
    const pointerInside =
      state.owner.matches(':hover') ||
      Boolean(state.isOverflow && (this.#moreButton?.matches(':hover') || this.#overflowSurface?.matches(':hover')));

    return focusInside || pointerInside;
  }

  #connectOverflowSurface() {
    const surface = this.overflowMenu;
    if (!surface || surface === this.#overflowSurface) return;

    this.#disconnectOverflowSurface();
    this.#overflowSurface = surface;
    surface.addEventListener('pointerenter', this.#onOverflowEnter);
    surface.addEventListener('pointerleave', this.#onOverflowLeave);
    surface.addEventListener('focusin', this.#onOverflowFocusIn);
    surface.addEventListener('focusout', this.#onOverflowFocusOut);
  }

  #disconnectOverflowSurface() {
    if (!this.#overflowSurface) return;
    this.#overflowSurface.removeEventListener('pointerenter', this.#onOverflowEnter);
    this.#overflowSurface.removeEventListener('pointerleave', this.#onOverflowLeave);
    this.#overflowSurface.removeEventListener('focusin', this.#onOverflowFocusIn);
    this.#overflowSurface.removeEventListener('focusout', this.#onOverflowFocusOut);
    this.#overflowSurface = null;
  }

  #onOverflowEnter = () => {
    if (this.#state?.isOverflow) this.#cancelClose();
  };

  #onOverflowLeave = (event) => {
    const state = this.#state;
    if (!state?.isOverflow) return;
    if (event.relatedTarget instanceof Node && this.#isNodeInsideState(event.relatedTarget, state)) return;
    this.#scheduleClose(state);
  };

  #onOverflowFocusIn = () => {
    if (this.#state?.isOverflow) this.#cancelClose();
  };

  #onOverflowFocusOut = (event) => {
    const state = this.#state;
    if (!state?.isOverflow) return;
    if (event.relatedTarget instanceof Node && this.#isNodeInsideState(event.relatedTarget, state)) return;
    this.#scheduleClose(state);
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Measure and write geometry only for the currently active generation.
   * @param {ActiveState} state
   */
  #syncGeometry(state) {
    if (!this.#isCurrentState(state) || !this.headerComponent) return;

    let height = state.submenu?.offsetHeight ?? 0;

    if (state.isOverflow) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      height = state.hasOwnSubmenu
        ? Math.max(this.overflowMenu?.offsetHeight ?? 0, overflowListHeight)
        : overflowListHeight;
    }

    if (!state.submenu) height = 0;

    this.headerComponent.style.setProperty('--submenu-height', `${height}px`);
    this.#setFullOpenHeaderHeight(height);
  }

  /**
   * Queue a post-layout measurement with an owner/generation stale guard.
   * @param {ActiveState} state
   */
  #queueGeometrySync(state) {
    this.#cancelGeometryFrame();
    const { owner, generation } = state;
    this.#geometryFrame = requestAnimationFrame(() => {
      this.#geometryFrame = undefined;
      const current = this.#state;
      if (!current || current.owner !== owner || current.generation !== generation) return;
      this.#syncGeometry(current);
    });
  }

  #cancelGeometryFrame() {
    if (this.#geometryFrame === undefined) return;
    cancelAnimationFrame(this.#geometryFrame);
    this.#geometryFrame = undefined;
  }

  /**
   * Keep clipping and height synchronized as active content, images, or overflow
   * layout change. The captured generation rejects callbacks queued before a switch.
   * @param {ActiveState} state
   */
  #observeSubmenuSize(state) {
    this.#cleanupResizeObserver();
    if (typeof ResizeObserver === 'undefined' || !state.submenu) return;

    this.#submenuResizeObserver = new ResizeObserver(() => {
      if (!this.#isCurrentState(state) || !state.submenu?.hasAttribute('data-active')) return;
      this.#queueGeometrySync(state);
    });
    this.#submenuResizeObserver.observe(state.submenu);

    if (state.isOverflow && this.overflowMenu && this.overflowMenu !== state.submenu) {
      this.#submenuResizeObserver.observe(this.overflowMenu);
    }
  }

  #cleanupResizeObserver() {
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find a top-level menu trigger inside an owner.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuTrigger(element) {
  if (!(element instanceof Element)) return null;
  const trigger = element.querySelector('[ref="menuitem"]');
  return trigger instanceof HTMLElement ? trigger : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
